import { PublicHostedAuthSession } from '@unipile/provider-shared';
import type { ProviderAuthenticationIntentResponse, SolveCheckpointBody } from '@unipile/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '../../../libs/axios';
import type { MutationConfig } from '../../../types/react-query-utility';

/**
 * This hook must live in UI package because it's used in provider packages.
 */

export const solveCheckpoint = async (
  { data }: { data: SolveCheckpointBody },
  { signal }: { signal?: AbortSignal } = {},
): Promise<ProviderAuthenticationIntentResponse> => {
  const response = await axiosInstance.post<ProviderAuthenticationIntentResponse>(`/v2/auth/checkpoint`, data, {
    signal,
  });

  return response.data;
};

type UseSolveCheckpointOptions = {
  mutationConfig?: MutationConfig<typeof solveCheckpoint>;
  /**
   * Resolved when the mutation runs. Use when the abort controller is recreated (e.g. in an effect)
   * so Strict Mode’s effect re-run does not keep using an already-aborted signal.
   */
  getSignal?: () => AbortSignal | undefined;
};

export const useSolveCheckpoint = ({ mutationConfig, getSignal }: UseSolveCheckpointOptions = {}) => {
  const { onSuccess, ...restConfig } = mutationConfig || {};

  const q = useQueryClient();

  return useMutation({
    onSuccess: (...args) => {
      onSuccess?.(...args);
    },
    ...restConfig,
    mutationFn: ({ data }: { data: Omit<SolveCheckpointBody, 'state'> }): Promise<ProviderAuthenticationIntentResponse> => {
      // Result of useHostedAuthPayload should be in react-query's cache as it's the first query made when loading the page
      const session = q.getQueryData<PublicHostedAuthSession>(['session']);

      if (!session) throw new Error('Session not in cache');

      return solveCheckpoint(
        {
          data: { ...data, ...(session.state && { state: session.state }) },
        },
        { signal: getSignal?.() },
      );
    },
  });
};
